import { useState } from 'react';
import { useUpdateEmail } from '@/hooks/useUserManagement';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Mail, Pencil, X } from 'lucide-react';
import { apiErrorMessage } from '@/utils/helpers';

/**
 * A user's email; the CEO can change it. The change goes to LDAP first (where every login
 * and sync reads it), then to the app.
 */
export function EmailField({ userId, email, canEdit }: { userId: string; email: string; canEdit: boolean }) {
  const updateEmail = useUpdateEmail();
  const [saved, setSaved] = useState(email);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (draft === null) return;
    setError(null);
    try {
      const user = await updateEmail.mutateAsync({ userId, email: draft.trim() });
      setSaved(user.email);
      setDraft(null);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  if (draft === null) {
    return (
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground" />
        {saved}
        {canEdit && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setDraft(saved)} title="Change the email (in LDAP and the app)">
            <Pencil className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-1">
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
        <Input type="email" value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8" autoFocus required />
        <Button type="submit" size="icon" className="h-8 w-8 shrink-0" disabled={updateEmail.isPending || !draft.trim() || draft.trim() === saved} title="Save">
          <Check className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => {
            setDraft(null);
            setError(null);
          }}
          title="Cancel"
        >
          <X className="h-4 w-4" />
        </Button>
      </form>
      <p className="text-xs text-muted-foreground">Saved in LDAP, so the next login and sync keep it.</p>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
